import { type FC, type PropsWithChildren, useEffect } from 'react';
import { ENV } from '@common/app/env';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';

if (ENV.POSTHOG_KEY) {
  posthog.init(ENV.POSTHOG_KEY, {
    api_host: ENV.POSTHOG_HOST || 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: false,
    capture_performance: true,
    capture_dead_clicks: true,
    autocapture: true,
    capture_exceptions: true,
    before_send: (event) => {
      if (!event || event.event !== '$exception') {
        return event;
      }

      const list = event.properties?.$exception_list;
      const first = Array.isArray(list) ? list[0] : null;
      const value: string = first?.value ?? '';

      // Drop synthetic exceptions wrapping RTK Query errors.
      // Two shapes: { status, data } (server) or { status, error } (network/parse).
      // These are unhandled API rejections, not real crashes.
      if (
        first?.mechanism?.synthetic === true &&
        typeof value === 'string' &&
        /keys:.*\bstatus\b/.test(value) &&
        /keys:.*\b(data|error)\b/.test(value)
      ) {
        return null;
      }

      // Drop expected user-flow rejections: wallet cancels, declined payments, etc.
      const expectedBusinessErrors = [
        'Transaction failed',
        'Payment cancelled',
        'Payment failed',
      ];

      if (typeof value === 'string' && expectedBusinessErrors.includes(value)) {
        return null;
      }

      // Drop TON Connect SDK errors (user closed wallet, connection aborted, etc.)
      if (
        typeof value === 'string' &&
        value.includes('[TON_CONNECT_SDK_ERROR]')
      ) {
        return null;
      }

      // Drop network-level fetch failures with no stack frames.
      // Safari: "Load failed", Chrome: "Failed to fetch",
      // Firefox: "NetworkError when attempting to fetch resource".
      // These are connectivity issues (offline, backgrounded tab, dropped Wi-Fi),
      // not bugs — and without a stack we can't action them anyway.
      const networkFetchErrors = [
        'Load failed',
        'Failed to fetch',
        'NetworkError when attempting to fetch resource.',
        'cancelled',
        'The network connection was lost.',
      ];
      const frames = first?.stacktrace?.frames;
      const hasNoStack = !Array.isArray(frames) || frames.length === 0;

      if (
        first?.type === 'TypeError' &&
        typeof value === 'string' &&
        networkFetchErrors.includes(value) &&
        hasNoStack
      ) {
        return null;
      }

      // Drop JSON parse failures with no stack frames.
      // Safari: "Unexpected EOF" / "JSON Parse error",
      // Chrome: "Unexpected end of JSON input".
      // Almost always caused by parsing an empty or truncated response body
      // (mobile network drop, backgrounded tab) — not actionable without a stack.
      const jsonParseErrors = [
        'Unexpected EOF',
        'Unexpected end of JSON input',
        'JSON Parse error: Unexpected EOF',
      ];

      if (
        first?.type === 'SyntaxError' &&
        typeof value === 'string' &&
        jsonParseErrors.some((msg) => value.includes(msg)) &&
        hasNoStack
      ) {
        return null;
      }

      return event;
    },
    loaded: (ph) => {
      // if (ENV.IS_DEV) {
      //   ph.opt_out_capturing();
      // } else {
      ph.opt_in_capturing();
      // }
      ph.register({
        app_version: __APP_VERSION__,
      });
    },
  });
}

const PostHogProvider: FC<PropsWithChildren> = ({ children }) => {
  useEffect(() => {
    const handleRouteChange = (): void => {
      posthog.capture('$pageview');
    };

    window.addEventListener('popstate', handleRouteChange);

    return (): void => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  if (!ENV.POSTHOG_KEY) {
    return <>{children}</>;
  }

  return <PHProvider client={posthog}>{children}</PHProvider>;
};

export { PostHogProvider };
